import { useEffect, useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import FileUploadButton from "@/components/admin/FileUploadButton";
import { resolveGoogleLink, GOOGLE_HINT } from "@/components/admin/googleLink";

const EMPTY = { title: "", date: "", time: "", location: "", description: "", image_url: "", free_entry: true, price_info: "" };
const inputClass = "w-full rounded-lg border border-stone-300 bg-[#FAF6F0] p-3 text-sm focus:border-[#B83227] focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-bold uppercase text-stone-700";

function safeDate(dateStr) {
  try {
    return format(new Date(dateStr), "d MMMM yyyy", { locale: fr });
  } catch {
    return dateStr;
  }
}

export default function AgendaManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/events").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/events/${editingId}`, form);
        toast.success("Date mise à jour");
      } else {
        await api.post("/admin/events", form);
        toast.success("Date ajoutée à l'agenda");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      date: item.date,
      time: item.time || "",
      location: item.location || "",
      description: item.description || "",
      image_url: item.image_url || "",
      free_entry: item.free_entry,
      price_info: item.price_info || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette date de l'agenda ?")) return;
    await api.delete(`/admin/events/${id}`).catch(() => {});
    toast.success("Date supprimée");
    load();
  };

  return (
    <div data-testid="agenda-manager" className="grid grid-cols-1 gap-10 lg:grid-cols-2">
      <form onSubmit={submit} className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">
          {editingId ? "Modifier la date" : "Ajouter une date"}
        </h2>
        <div>
          <label className={labelClass}>Nom de l'événement *</label>
          <input data-testid="event-title-input" className={inputClass} placeholder="Ex : Bal trad de la Fête de la Mer" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Date *</label>
            <input data-testid="event-date-input" type="date" className={inputClass} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
          </div>
          <div>
            <label className={labelClass}>Heure</label>
            <input data-testid="event-time-input" type="time" className={inputClass} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
          </div>
        </div>
        <div>
          <label className={labelClass}>Lieu</label>
          <input data-testid="event-location-input" className={inputClass} placeholder="Ex : Esplanade du Port, Salle polyvalente..." value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        </div>
        <div>
          <label className={labelClass}>Précisions (facultatif)</label>
          <textarea data-testid="event-description-input" rows="3" className={inputClass} placeholder="Ex : initiation à 18h, bal à 21h..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div>
          <label className={labelClass}>Affiche ou photo (facultatif)</label>
          <input data-testid="event-image-input" className={inputClass} placeholder="https://..." value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} onBlur={(e) => resolveGoogleLink(e.target.value, (u) => setForm((f) => ({ ...f, image_url: u })))} />
          <p className="mt-1 text-xs text-stone-500">{GOOGLE_HINT}</p>
          <div className="mt-2">
            <FileUploadButton testId="event-image-upload" accept="image/*" label="Ou téléverser l'affiche" onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-stone-700">
          <input data-testid="event-free-checkbox" type="checkbox" checked={form.free_entry} onChange={(e) => setForm({ ...form, free_entry: e.target.checked })} className="h-4 w-4 accent-[#B83227]" />
          Entrée libre
        </label>
        {!form.free_entry && (
          <div>
            <label className={labelClass}>Tarif</label>
            <input data-testid="event-price-input" className={inputClass} placeholder="Ex : 8 €, gratuit -12 ans" value={form.price_info} onChange={(e) => setForm({ ...form, price_info: e.target.value })} />
          </div>
        )}
        <div className="flex gap-3">
          <button type="submit" data-testid="event-save-button" disabled={saving} className="rounded bg-[#B83227] px-6 py-2.5 text-xs font-bold uppercase text-white hover:bg-[#96281E] disabled:opacity-60">
            {saving ? "Enregistrement..." : editingId ? "Mettre à jour" : "Ajouter"}
          </button>
          {editingId && (
            <button type="button" data-testid="event-cancel-button" onClick={() => { setEditingId(null); setForm(EMPTY); }} className="rounded border border-stone-300 px-6 py-2.5 text-xs font-bold uppercase text-stone-600">
              Annuler
            </button>
          )}
        </div>
      </form>

      <div className="space-y-4">
        <h2 className="font-display text-xl font-bold uppercase text-[#1E1B18]">Dates à l'agenda ({items.length})</h2>
        {items.length === 0 && <p className="text-sm text-stone-500">Aucune date. Les dates passées disparaissent automatiquement du site public.</p>}
        {items.map((ev) => (
          <div key={ev.id} data-testid={`event-item-${ev.id}`} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-[#1E1B18]">{ev.title}</p>
                <p className="text-xs text-stone-500">
                  {safeDate(ev.date)}{ev.time && ` · ${ev.time}`}{ev.location && ` · ${ev.location}`} · {ev.free_entry ? "Entrée libre" : ev.price_info || "Payant"}
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => edit(ev)} data-testid={`event-edit-${ev.id}`} className="rounded border border-stone-300 p-2 text-stone-600 hover:border-stone-500" aria-label="Modifier">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => remove(ev.id)} data-testid={`event-delete-${ev.id}`} className="rounded bg-red-50 p-2 text-red-700 hover:bg-red-100" aria-label="Supprimer">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
